import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  Clock,
  CreditCard,
  Crown,
  Headphones,
  Loader2,
  Lock,
  Shield,
  Sparkles,
  Users,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";
import { toast } from "sonner";

declare global { interface Window { Razorpay: any; } }

const RAZORPAY_KEY_ID = "rzp_live_SMLv7nKxw6OBDt";
const RAZORPAY_SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_SCRIPT_ID = "razorpay-checkout-sdk";

const cleanupRazorpaySdk = () => {
  const script = document.getElementById(RAZORPAY_SCRIPT_ID);
  if (script?.parentNode) {
    script.parentNode.removeChild(script);
  }
  delete (window as any).Razorpay;
};

const ensureRazorpayLoaded = async (): Promise<void> => {
  if (window.Razorpay) return;

  const existing = document.querySelector<HTMLScriptElement>(`script#${RAZORPAY_SCRIPT_ID}[src="${RAZORPAY_SDK_URL}"]`);
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay SDK")), { once: true });
      setTimeout(() => (window.Razorpay ? resolve() : reject(new Error("Razorpay SDK load timeout"))), 6000);
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = RAZORPAY_SCRIPT_ID;
    script.src = RAZORPAY_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.head.appendChild(script);
  });
};

interface Plan {
  tier: string;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: string;
  popular?: boolean;
  accent: string;
  summary: string;
}

const plans: Plan[] = [
  {
    tier: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    tagline: "For individuals trying MeetHub.",
    features: [
      "10 meetings per month",
      "Up to 100 participants",
      "1-hour recording length",
      "Recordings kept 2 days",
      "Live captions & chat",
      "Browser-based — no installs",
    ],
    cta: "Start free",
    accent: "Personal",
    summary: "Try the full meeting experience without payment setup.",
  },
  {
    tier: "starter",
    name: "Starter",
    price: "$8",
    period: "user / month",
    tagline: "For freelancers running client meetings.",
    features: [
      "Unlimited meetings",
      "4-hour recordings",
      "30-day recording retention",
      "Branded meeting links",
      "Email support",
    ],
    cta: "Get Starter",
    accent: "Client-ready",
    summary: "A practical step up for unlimited client and project calls.",
  },
  {
    tier: "professional",
    name: "Professional",
    price: "$15",
    period: "user / month",
    tagline: "For growing teams that meet often.",
    features: [
      "Everything in Starter",
      "Unlimited recording length",
      "90-day retention",
      "Breakout rooms & polls",
      "Webinar mode",
      "Priority support",
    ],
    cta: "Get Professional",
    popular: true,
    accent: "Team scale",
    summary: "More recording power, team workflows, and live engagement tools.",
  },
  {
    tier: "business",
    name: "Business",
    price: "$25",
    period: "user / month",
    tagline: "For organizations with serious meeting needs.",
    features: [
      "Everything in Professional",
      "1-year recording retention",
      "Meeting analytics",
      "Admin & SSO controls",
      "Dedicated account manager",
    ],
    cta: "Get Business",
    accent: "Organization",
    summary: "Admin controls, analytics, and longer retention for serious teams.",
  },
];

const heroStats = [
  { label: "Start at", value: "$0" },
  { label: "Room size", value: "100" },
  { label: "Contracts", value: "None" },
];

const planMetrics: Record<string, { icon: typeof Video; label: string; value: string }[]> = {
  free: [
    { icon: Video, label: "Meetings", value: "10 / month" },
    { icon: Users, label: "Participants", value: "100" },
    { icon: Clock, label: "Retention", value: "2 days" },
  ],
  starter: [
    { icon: Video, label: "Meetings", value: "Unlimited" },
    { icon: Clock, label: "Recording", value: "4 hours" },
    { icon: Headphones, label: "Support", value: "Email" },
  ],
  professional: [
    { icon: Video, label: "Meetings", value: "Unlimited" },
    { icon: Clock, label: "Retention", value: "90 days" },
    { icon: BarChart3, label: "Tools", value: "Webinars" },
  ],
  business: [
    { icon: Clock, label: "Retention", value: "1 year" },
    { icon: Shield, label: "Controls", value: "Admin + SSO" },
    { icon: Headphones, label: "Support", value: "Dedicated" },
  ],
};

const includedHighlights = [
  {
    icon: Video,
    title: "Browser-first meetings",
    description: "HD calls, chat, captions, and secure joining are included from day one.",
  },
  {
    icon: Lock,
    title: "Security baseline",
    description: "Protected room access, host controls, and privacy-minded defaults across every plan.",
  },
  {
    icon: CreditCard,
    title: "Flexible billing",
    description: "Start free, move plans when your team changes, and cancel without long contracts.",
  },
];

const faqs = [
  { q: "Can I switch plans anytime?", a: "Yes. Upgrade or downgrade at any time. Changes apply immediately and we pro-rate the difference." },
  { q: "Is there a free trial of paid plans?", a: "The Free plan never expires and includes most features. Paid plans add unlimited meetings and longer recording retention." },
  { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards via Razorpay, plus UPI, Netbanking, and wallets." },
  { q: "Do you offer discounts for annual billing?", a: "Yes — contact us for annual pricing with up to 20% off." },
  { q: "Can I cancel my subscription?", a: "Anytime. There are no long-term contracts and your access continues until the end of the billing period." },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    plan_tier: string | null;
    subscription_end: string | null;
  } | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) await checkSubscription();
      else setChecking(false);
    };
    init();
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(async (_e, session) => {
      setUser(session?.user ?? null);
      if (session?.user) await checkSubscription();
      else { setSubscription(null); setChecking(false); }
    });
    return () => authSub.unsubscribe();
  }, []);

  const checkSubscription = async () => {
    try {
      setChecking(true);
      const headers = await getSupabaseAuthHeaders();
      if (!headers) { setSubscription(null); return; }
      const { data, error } = await supabase.functions.invoke('check-subscription', { headers });
      if (error) throw error;
      setSubscription(data);
    } catch (e) { console.error(e); }
    finally { setChecking(false); }
  };

  const currentTier = subscription?.subscribed ? subscription.plan_tier : "free";

  const handleSubscribe = async (tier: string) => {
    if (tier === "free") { navigate("/register"); return; }
    if (!user) { toast.info("Sign in to subscribe"); navigate("/login"); return; }
    try {
      setLoadingPlan(tier);
      await ensureRazorpayLoaded();
      const { data, error } = await supabase.functions.invoke('create-razorpay-order', { body: { planTier: tier } });
      if (error) throw error;
      if (!data?.subscription_id) throw new Error("Failed to create subscription");
      const options = {
        key: RAZORPAY_KEY_ID,
        subscription_id: data.subscription_id,
        name: "MeetHub",
        description: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan — Monthly`,
        prefill: { email: data.user_email, name: data.user_name },
        theme: { color: "#2563eb" },
        handler: async (response: any) => {
          try {
            const { data: vd, error: ve } = await supabase.functions.invoke('verify-razorpay-payment', {
              body: {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan_tier: tier,
              },
            });
            if (ve) throw ve;
            if (vd?.success) { toast.success("Subscription activated!"); await checkSubscription(); }
            else throw new Error("Payment verification failed");
          } catch (err: any) { toast.error(err.message || "Verification failed"); }
        },
        modal: {
          ondismiss: () => {
            setLoadingPlan(null);
            cleanupRazorpaySdk();
          },
        },
      };
      if (!window.Razorpay) throw new Error("Razorpay SDK not loaded");
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp: any) => {
        toast.error(resp.error?.description || "Payment failed");
        setLoadingPlan(null);
        cleanupRazorpaySdk();
      });
      rzp.open();
    } catch (e: any) {
      toast.error(e.message || "Checkout error");
      cleanupRazorpaySdk();
    } finally { setLoadingPlan(null); }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="Pricing — Simple plans for every team · MeetHub"
        description="Free for individuals. Affordable per-seat pricing for teams. All MeetHub plans include HD video, recording, breakout rooms, and live captions."
        canonicalPath="/pricing"
        jsonLd={jsonLd}
      />
      <Navigation />

      <section className="relative overflow-hidden bg-gradient-soft pt-24 pb-10 sm:pt-24 sm:pb-12 border-b">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="container relative max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_0.95fr] gap-10 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Pricing</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                Simple plans for every meeting workflow.
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-xl">
                Start free, keep your meetings professional, and upgrade only when your team needs longer recordings, retention, or admin controls.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="h-12 px-7" onClick={() => navigate("/register")}>
                  Start free <ArrowRight className="h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="h-12 px-7" onClick={() => navigate("/contact")}>
                  Talk to sales
                </Button>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[430px]">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1100&q=80"
                alt="Team reviewing subscription plans"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/45 to-foreground/10" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                  Scale when ready
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                  Start small, then add recording, retention, and admin depth.
                </h2>
                <p className="mt-3 text-sm sm:text-base text-primary-foreground/85 max-w-md leading-relaxed">
                  Plans are organized around how often your team meets, how long you need records, and how much control admins need.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {heroStats.map((stat) => (
                    <div key={stat.label} className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur">
                      <p className="text-lg font-semibold">{stat.value}</p>
                      <p className="mt-1 text-xs text-primary-foreground/75">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-16 sm:pt-16 sm:pb-14">
        <div className="container max-w-7xl">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Plans</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">Choose the plan that fits your team.</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Every plan includes browser meetings, captions, chat, and secure joining. Upgrade when you need more recording power.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const isCurrent = currentTier === plan.tier;
              const loading = loadingPlan === plan.tier;
              return (
                <Card
                  key={plan.tier}
                  className={`group relative flex flex-col overflow-hidden border-border/70 transition-all hover:border-primary/30 hover:shadow-lg ${
                    plan.popular ? "border-primary shadow-lg ring-1 ring-primary/20" : ""
                  }`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 shadow-sm">
                      <Sparkles className="h-3 w-3 mr-1" /> Most popular
                    </Badge>
                  )}
                  <CardContent className="flex flex-col p-6 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-primary">{plan.accent}</p>
                        <h3 className="mt-1 font-semibold text-xl">{plan.name}</h3>
                      </div>
                      <div className={`h-11 w-11 rounded-xl inline-flex items-center justify-center ${
                        plan.popular ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
                      }`}>
                        {plan.tier === "business" ? <Crown className="h-5 w-5" /> : <Video className="h-5 w-5" />}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{plan.tagline}</p>
                    <div className="mt-5">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-muted-foreground text-sm ml-1">/ {plan.period}</span>
                    </div>
                    <p className="mt-3 min-h-[40px] text-sm text-muted-foreground leading-relaxed">{plan.summary}</p>
                    <div className="mt-5 grid gap-2">
                      {planMetrics[plan.tier].map((metric) => (
                        <div key={metric.label} className="flex items-center justify-between rounded-xl border bg-secondary/40 px-3 py-2">
                          <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                            <metric.icon className="h-3.5 w-3.5 text-primary" />
                            {metric.label}
                          </span>
                          <span className="text-xs font-semibold">{metric.value}</span>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="w-full mt-6 h-11"
                      variant={plan.popular ? "default" : "outline"}
                      disabled={isCurrent || loading || checking}
                      onClick={() => handleSubscribe(plan.tier)}
                    >
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isCurrent ? "Current plan" : plan.cta}
                    </Button>
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch">
            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[460px]">
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1100&q=80"
                alt="Team planning meeting workflows"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                  Included everywhere
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                  Core meeting tools are not locked behind the top plan.
                </h2>
                <p className="mt-3 text-sm sm:text-base text-primary-foreground/85 max-w-md leading-relaxed">
                  MeetHub keeps the essentials available so teams can evaluate the product through real meetings.
                </p>
              </div>
            </div>
            <div className="grid gap-4">
              {includedHighlights.map((item, index) => (
                <Card
                  key={item.title}
                  className={`border-border/70 transition-all hover:border-primary/30 hover:shadow-md ${
                    index === 0 ? "bg-gradient-soft" : "bg-card"
                  }`}
                >
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{item.title}</h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="rounded-2xl border bg-card p-5 sm:p-6 shadow-sm">
                <div className="grid grid-cols-3 gap-3">
                  {["No installs", "Secure links", "Cancel anytime"].map((item) => (
                    <div key={item} className="rounded-xl border bg-secondary/40 px-3 py-3 text-center text-xs font-semibold">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="rounded-2xl border bg-foreground text-background overflow-hidden shadow-lg">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
              <div className="p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-background/15">
                <Badge className="mb-5 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                  Enterprise
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Need more than Business?</h2>
                <p className="mt-4 text-sm sm:text-base text-background/75 leading-relaxed">
                  Custom contracts, SSO/SAML, dedicated infra, and a real account manager.
                  Let's talk about what you need.
                </p>
                <Button size="lg" variant="secondary" className="mt-8 h-12 px-7" onClick={() => navigate("/contact")}>
                  Talk to sales <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="bg-background text-foreground p-5 sm:p-6 lg:p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: "Identity", value: "SSO/SAML", icon: Shield },
                    { title: "Contracts", value: "Custom terms", icon: CreditCard },
                    { title: "Infrastructure", value: "Dedicated options", icon: Lock },
                    { title: "Success", value: "Account manager", icon: Headphones },
                  ].map((item) => (
                    <Card key={item.title} className="border-border/70 bg-card hover:border-primary/30 hover:shadow-md transition-all">
                      <CardContent className="p-5">
                        <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center">
                          <item.icon className="h-5 w-5" />
                        </div>
                        <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-primary">{item.title}</p>
                        <h3 className="mt-1 font-semibold text-lg">{item.value}</h3>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
            <div className="rounded-2xl border bg-card p-6 sm:p-8 shadow-sm">
              <Badge variant="outline" className="mb-4">FAQ</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Pricing FAQ</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Clear answers about plan changes, trials, billing methods, annual discounts, and cancellation.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {["Free forever", "UPI ready", "Annual options", "No contracts"].map((item) => (
                  <div key={item} className="rounded-xl border bg-secondary/50 px-4 py-3 text-sm font-medium">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <Card className="shadow-sm">
              <CardContent className="p-5 sm:p-6">
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`p-${i}`} className="rounded-xl border px-4">
                      <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}




